import { Download } from 'lucide-react'
import { useState } from 'react'
import { downloadApiFile } from '../api/client'
import { useToast } from '../components/ToastProvider'
import { Button } from '../components/ui'
import { errorMessage, MONTHS } from '../utils/format'

type ScheduleFormat = 'xlsx' | 'xls'

/** Источник месячного ППР: подтверждённый годовой ППР (месяц выбирается) или сформированный месячный план (месяц задан). */
export type ScheduleSource = { kind: 'document'; documentId: string } | { kind: 'plan'; planId: string; month: number }

/**
 * Выгрузка месячного ППР в формате графика заказчика (книга Excel: листы по системам, дни месяца, отметки «+»).
 * Даты совпадают с планом и заданиями месяца; файл можно передать заказчику или загрузить обратно как месячный график.
 */
export function MonthlyScheduleExport({ source, objectCode, year }: { source: ScheduleSource; objectCode?: string | null; year?: number | null }) {
  const toast = useToast()
  const [month, setMonth] = useState(() => (source.kind === 'plan' ? source.month : new Date().getMonth() + 1))
  const [format, setFormat] = useState<ScheduleFormat>('xlsx')
  const [busy, setBusy] = useState(false)
  const scheduleYear = year ?? new Date().getFullYear()
  const monthName = MONTHS[month - 1] ?? String(month)
  const path = source.kind === 'plan' ? `/plans/${source.planId}/schedule?format=${format}` : `/import/ppr/${source.documentId}/monthly-schedule?month=${month}&format=${format}`

  async function exportSchedule() {
    setBusy(true)
    try {
      await downloadApiFile(path, `ППР ${objectCode || 'график'} ${monthName} ${scheduleYear}.${format}`)
      toast.show(`Месячный ППР на ${monthName.toLocaleLowerCase('ru')} ${scheduleYear} сохранён (${format.toUpperCase()}).`, 'success')
    } catch (caught) {
      toast.show(errorMessage(caught), 'error')
    } finally {
      setBusy(false)
    }
  }

  return <section className="surface" aria-label="Выгрузка месячного ППР">
    <div className="section-heading"><div><h3>Выгрузка месячного ППР</h3><p>{source.kind === 'plan'
      ? 'Книга Excel в том же формате, в котором загружался месячный график заказчика: листы по системам, дни месяца и отметки «+». Даты берутся из позиций этого плана — те же, что у заданий.'
      : 'Книга Excel в том же формате, в котором загружался месячный график заказчика: листы по системам, дни месяца и отметки «+». Если план на месяц уже сформирован, даты берутся из него; иначе рассчитываются по частоте работ. Файл можно передать заказчику или загрузить обратно как месячный график.'}</p></div></div>
    <div className="row-actions table-toolbar--wrap">
      {source.kind === 'document'
        ? <label className="compact-select"><select name="scheduleMonth" aria-label="Месяц графика" value={month} onChange={(event) => setMonth(Number(event.target.value))}>{MONTHS.map((name, index) => <option key={name} value={index + 1}>{name} {scheduleYear}</option>)}</select></label>
        : <span className="toolbar-count">{monthName} {scheduleYear}</span>}
      <label className="compact-select"><select name="scheduleFormat" aria-label="Формат файла" value={format} onChange={(event) => setFormat(event.target.value === 'xls' ? 'xls' : 'xlsx')}><option value="xlsx">Excel (XLSX)</option><option value="xls">Excel 97-2003 (XLS)</option></select></label>
      <Button variant="secondary" loading={busy} onClick={() => void exportSchedule()}><Download size={16} />Скачать месячный ППР</Button>
    </div>
  </section>
}
